// Subscribe to the auth singleton's user/ready/recovery state with React
// semantics. Returns null until the first session resolution completes.
// `recovery` flips true when the user arrives via a password-reset link so
// App.tsx can route them to the ResetPasswordScreen.

import { useEffect, useState } from 'react';
import { getAuth, type AuthUser } from '@/lib/auth/auth';

export interface UseAuth {
  user: AuthUser | null;
  /** True once Supabase has resolved the initial session (signed in or out). */
  ready: boolean;
  /** True while the user is mid-password-reset (arrived via the email link). */
  recovery: boolean;
}

export function useAuth(): UseAuth {
  const auth = getAuth();
  const [user, setUser] = useState<AuthUser | null>(auth.current());
  const [ready, setReady] = useState(auth.ready());
  const [recovery, setRecovery] = useState(auth.isRecoveryMode());

  useEffect(() => {
    const unsubUser = auth.subscribe((u) => {
      setUser(u);
      setReady(auth.ready());
    });
    const unsubRecovery = auth.subscribeRecovery((r) => {
      setRecovery(r);
    });
    // Re-check ready on every poll tick until resolved (covers the case where
    // ready flips before our first subscribe handler fires).
    const t = window.setInterval(() => {
      if (!ready && auth.ready()) {
        setReady(true);
        window.clearInterval(t);
      }
    }, 80);
    return () => {
      unsubUser();
      unsubRecovery();
      window.clearInterval(t);
    };
  }, [auth, ready]);

  return { user, ready, recovery };
}
